import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, HandCoins } from "@/components/icons";
import api from "@/lib/api";
import { Page } from "@/components/PageConsole";
import { getErrorMessage } from "@/lib/errors";
import { formatEuro, formatNlDate } from "@/components/Badges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function RecoverableOverview() {
  const navigate = useNavigate();
  const [repairs, setRepairs] = useState([]);
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [locFilter, setLocFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [rep, ass, loc] = await Promise.all([
        api.get("/repairs?recoverable=true"),
        api.get("/assets"),
        api.get("/locations"),
      ]);
      setRepairs(rep.data);
      setAssets(ass.data);
      setLocations(loc.data);
    } catch (e) {
      setError(getErrorMessage(e, "Kan verhaalbare reparaties niet laden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const assetById = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);
  const locById = useMemo(() => Object.fromEntries(locations.map((l) => [l.location_id, l])), [locations]);

  // Group recoverable werkbonnen per location. Round 26 §1.5: a bon whose
  // rate is not set has NO total — do not silently count it as € 0,00. It
  // is counted apart, and the reader says so plainly.
  const groups = useMemo(() => {
    const map = {};
    for (const r of repairs) {
      if (locFilter !== "all" && r.location_id !== locFilter) continue;
      const key = r.location_id || "__none__";
      if (!map[key]) map[key] = {
        location: locById[key] || null,
        items: [], total: 0,
        tariefOntbreektCount: 0, tariefOntbreektUren: 0,
      };
      map[key].items.push(r);
      if (r.tarief_ontbreekt) {
        map[key].tariefOntbreektCount += 1;
        map[key].tariefOntbreektUren += r.uren_totaal || 0;
      } else {
        map[key].total += r.cost_total || 0;
      }
    }
    return Object.values(map).sort((a, b) =>
      (a.location?.name || "").localeCompare(b.location?.name || "")
    );
  }, [repairs, locFilter, locById]);

  const grandTotal = groups.reduce((s, g) => s + g.total, 0);

  // A zero is not what this screen is about, so it never becomes a headline.
  const consoleFacts = groups.length
    ? [`${formatEuro(grandTotal)} verhaalbaar`, `${groups.length} locaties`,
       `${repairs.length} reparaties`]
    : ["nog niets verhaalbaar", `${locations.length} locaties`];

  const locationsWithRecoverable = useMemo(() => {
    const ids = new Set(repairs.map((r) => r.location_id).filter(Boolean));
    return locations.filter((l) => ids.has(l.location_id));
  }, [repairs, locations]);

  if (loading) return <div className="page-body"><Card className="p-8 text-center text-body text-muted-foreground">Laden...</Card></div>;

  return (
    <Page console={{ variant: "view", title: "Verhaalbaar", facts: consoleFacts }}>
      <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <div className="w-56">
            <Select value={locFilter} onValueChange={setLocFilter}>
              <SelectTrigger data-testid="recoverable-location-filter"><SelectValue placeholder="Alle locaties" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle locaties</SelectItem>
                {locationsWithRecoverable.map((l) => (
                  <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="secondary" data-testid="recoverable-print-button" onClick={() => window.print()}>
            <Printer size={18} className="mr-2" /> Afdrukken
          </Button>
        </div>
      </div>

      {error && (
        <Card className="p-5">
          <p className="text-body text-destructive">{error}</p>
          <Button className="mt-3" variant="secondary" onClick={load}>Opnieuw proberen</Button>
        </Card>
      )}

      {groups.length > 0 && (
        <Card className="p-5">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-body text-muted-foreground">
              Totaal verhaalbaar
              <span className="ml-1 text-data">(alle getoonde locaties)</span>
            </span>
            <span className="font-mono text-display tabular-nums text-foreground" data-testid="recoverable-grand-total">
              {formatEuro(grandTotal)}
            </span>
          </div>
        </Card>
      )}

      {groups.length === 0 && !error && (
        <Card className="flex flex-col items-center gap-3 p-8 text-center" data-testid="recoverable-empty">
          <HandCoins size={28} className="text-muted-foreground" />
          <p className="max-w-md text-body text-foreground">
            Hier komen de reparaties te staan die je op de verhuurder kunt verhalen. Zet bij een
            reparatie op een gehuurde locatie in wat er gerepareerd is en het vinkje "verhaalbaar", dan
            verschijnt hij hier met bedrag en onderbouwing.
          </p>
          <Button variant="secondary" data-testid="recoverable-empty-action" onClick={() => navigate("/assets")}>
            Naar de assets
          </Button>
        </Card>
      )}

      {groups.map((g) => {
        const loc = g.location;
        const ll = loc?.landlord || {};
        return (
          <Card key={loc?.location_id || "none"} className="p-5" data-testid={`recoverable-group-${loc?.location_id || "none"}`}>
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-body font-semibold text-foreground">
                  {loc?.name || "Onbekende locatie"}
                </h2>
                {loc && (
                  <button
                    type="button"
                    className="text-data text-primary hover:underline print:hidden"
                    onClick={() => navigate(`/locaties/${loc.location_id}`)}
                  >
                    Bekijk locatie
                  </button>
                )}
              </div>
              <div className="text-right text-body">
                <div className="text-data uppercase tracking-wide text-muted-foreground">Totaal deze locatie</div>
                <div className="text-kop font-semibold tabular-nums text-foreground" data-testid={`recoverable-total-${loc?.location_id || "none"}`}>
                  {formatEuro(g.total)}
                </div>
                {g.tariefOntbreektCount > 0 && (
                  <div className="mt-1 text-data text-letop-tekst" data-testid={`recoverable-tarief-ontbreekt-${loc?.location_id || "none"}`}>
                    + {g.tariefOntbreektCount} {g.tariefOntbreektCount === 1 ? "bon" : "bonnen"}
                    {g.tariefOntbreektUren > 0 ? ` (${g.tariefOntbreektUren.toString().replace(".", ",")} uur)` : ""}
                    {" "}zonder tarief
                  </div>
                )}
              </div>
            </div>

            {/* Only when there is something to show: an empty landlord block was
                four labels above four voids. */}
            {loc?.ownership === "huur" && [ll.name, ll.contact, ll.email, ll.phone].some(Boolean) && (
              <div className="mb-4 grid grid-cols-1 gap-3 rounded-md border border-border bg-muted/30 p-3 sm:grid-cols-4">
                {[["Verhuurder", ll.name], ["Contactpersoon", ll.contact], ["E-mail", ll.email], ["Telefoon", ll.phone]]
                  .filter(([, v]) => Boolean(v))
                  .map(([label, v]) => (
                    <div key={label}>
                      <div className="text-data uppercase tracking-wide text-muted-foreground">{label}</div>
                      <div className="text-body text-foreground">{v}</div>
                    </div>
                  ))}
              </div>
            )}

            <div className="hidden sm:block print:block">
              <Table data-testid={`recoverable-table-${loc?.location_id || "none"}`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Wat gerepareerd</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead>Uitgevoerd door</TableHead>
                    <TableHead className="text-right">Bedrag (excl. btw)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.items.map((r) => {
                    const a = assetById[r.asset_id];
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="text-body tabular-nums">{formatNlDate(r.date)}</TableCell>
                        <TableCell className="text-body">
                          {a ? (
                            <button type="button" className="text-left hover:underline print:no-underline" onClick={() => navigate(`/assets/${a.id}`)}>
                              <span className="font-mono text-data">{a.asset_code}</span> {a.brand} {a.model}
                            </button>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-body">{r.demarcation_element}</TableCell>
                        <TableCell className="text-body">{r.description}</TableCell>
                        <TableCell className="text-body">{r.performed_by === "extern" ? `Extern${r.external_party ? " – " + r.external_party : ""}` : "TD"}</TableCell>
                        <TableCell className="text-right text-body tabular-nums">
                          {r.tarief_ontbreekt
                            ? <span className="text-data text-letop-tekst" data-testid={`recoverable-tarief-ontbreekt-${r.id}`}>tarief nog niet ingesteld · {r.uren_totaal ? `${r.uren_totaal} uur` : ""}</span>
                            : formatEuro(r.cost_total)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: stacked cards (asset → element/omschrijving → bedrag). */}
            <div className="divide-y divide-border sm:hidden print:hidden" data-testid={`recoverable-cardlist-${loc?.location_id || "none"}`}>
              {g.items.map((r) => {
                const a = assetById[r.asset_id];
                return (
                  <div key={r.id} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0 space-y-0.5">
                      <div className="text-data tabular-nums text-muted-foreground">{formatNlDate(r.date)}</div>
                      {a ? (
                        <button type="button" className="text-left text-body text-foreground hover:underline" onClick={() => navigate(`/assets/${a.id}`)}>
                          <span className="font-mono text-data">{a.asset_code}</span> {a.brand} {a.model}
                        </button>
                      ) : null}
                      <div className="text-data text-muted-foreground">
                        {[r.demarcation_element, r.description].filter(Boolean).join(" · ")}
                      </div>
                      <div className="text-data text-muted-foreground">{r.performed_by === "extern" ? `Extern${r.external_party ? " – " + r.external_party : ""}` : "TD"}</div>
                    </div>
                    <div className="shrink-0 text-right text-body font-semibold tabular-nums text-foreground">
                      {r.tarief_ontbreekt
                        ? <span className="text-data text-letop-tekst">tarief nog niet ingesteld</span>
                        : formatEuro(r.cost_total)}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
      </div>
    </Page>
  );
}
